import isNil from 'lodash/fp/isNil'
import { addLocaleData } from '@dragonraider5/react-intl'

import { reduceMessages } from './utils'

/** save shouldPolyfillIntl to load locale data when intl has polyfilled */
const shouldPolyfillIntl = isNil(window.Intl)

/**
 * polyfills intl with locale-data if needit
 *
 * @async
 * @param {String} locale - locale to load
 * @param {Boolean} [loadPolyfill=isNil(window.Intl)] - load intl polyfill and locale data
 * @return {Promise<module|true>} promise to load locale data
 */
export async function polyfillIntl(locale, loadPolyfill = shouldPolyfillIntl) {
  if (!loadPolyfill) {
    // resolve as true to dont break promise
    return Promise.resolve(true)
  }

  await import(
    /* webpackChunkName: 'intl' */
    /* webpackPreload: true */
    'intl'
  )
  return import(
    /* webpackChunkName: `intl-[request]` */
    /* webpackPreload: true */
    `./loaders/${locale}-polyfill`
  )
}

/**
 * polyfills intl if need it and return messages formats and react-intl locale data
 *
 * @param {String} locale - locale to load
 * @param {Boolean} [loadPolyfill=isNil(window.Intl)] - load intl polyfill and locale data
 * @return {Promise<module>} object with loaded modules
 */
export async function loader(locale, loadPolyfill = shouldPolyfillIntl) {
  const intlPolyfillPromise = await polyfillIntl(locale, loadPolyfill)
  const imports = [
    intlPolyfillPromise,
    import(
      /* webpackChunkName: `app-intl-[request]` */
      /* webpackPreload: true */
      `./loaders/${locale}`
    )
  ]

  /* eslint-disable-next-line no-unused-vars  */
  const [_, modules] = await Promise.all(imports)

  return modules
}

/**
 * loads resources for react-intl
 *
 * @async
 * @param {String} locale - locale to load
 * @param {Boolean} [loadPolyfill=isNil(window.Intl)] - load intl polyfill and locale data
 * @return {Promise<Object>} messages and formats to pass to IntlProvider
 */
export default async function intlLoader(locale, loadPolyfill = shouldPolyfillIntl) {
  const {
    messages,
    formats,
    localeData
  } = await loader(locale, loadPolyfill)

  // add locale data to react-intl
  addLocaleData(localeData)

  // return messages and formats to pass to IntlProvider
  return {
    messages: reduceMessages(messages),
    formats
  }
}
